import React, { useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, XCircle, BrainCircuit, Loader2, AlertTriangle, FileText, Upload, X, ChevronDown } from 'lucide-react';
import { api } from '../../api/apiClient';

// Max external context sent to the AI (chars). Server enforces 6000 defensively.
const EXTERNAL_MAX = 4000;
const IMPORT_MAX_BYTES = 200 * 1024;
// Non-blocking heuristic: warn that the text will be treated as data, not instructions.
const SUSPICIOUS_PATTERN = /ignore\s+(les\s+|tes\s+|toutes\s+)?(instructions|consignes)|oublie\s+tout|<\s*system|system\s*:|DAN\b|jailbreak|r[eé]v[eè]le|reveal\s+(the\s+)?(system\s+)?prompt|exfiltre/i;

const PHASES = ['CONNAITRE', 'RENTRER', 'TROUVER', 'EXPLOITER'];

const severityColor = (severity) => {
  const s = String(severity || '').toLowerCase();
  if (s.includes('haute') || s.includes('high') || s.includes('critique')) return 'bg-red-100 text-red-700 border-red-200';
  if (s.includes('moyenne') || s.includes('medium')) return 'bg-amber-100 text-amber-700 border-amber-200';
  return 'bg-slate-100 text-slate-600 border-slate-200';
};

/**
 * ModeComparisonPanel - Compares manually entered modes vs Assistant ACG generated modes
 * Shows phase coverage per group, action overlap, and an optional general
 * AI security analysis of the combined mode set (persisted on the scenario).
 */
const ModeComparisonPanel = ({
  manualModes = [],
  aiModes = [],
  operationalSteps = [],
  elementaryActions = [],
  analysisId,
  attackPath = null,
  businessContext = null,
  aiEnabled = false,
  savedCritique = null,
  onSaveCritique = null,
}) => {
  const { t } = useTranslation();
  const [isComparing, setIsComparing] = useState(false);
  const [critique, setCritique] = useState(null);
  const [critiqueError, setCritiqueError] = useState(null);
  // External analyst context (ephemeral session-only, never persisted in DB)
  const [extraContext, setExtraContext] = useState('');
  const [showExternal, setShowExternal] = useState(false);
  const [importError, setImportError] = useState(null);
  const fileInputRef = useRef(null);

  const stats = useMemo(() => {
    // Index every step under all its id forms — manual paths may reference id/_id,
    // AI paths reference elementaryActionId (same matching as the flow visualization)
    const stepsById = new Map();
    [...operationalSteps, ...elementaryActions].forEach((s) => {
      [s.elementaryActionId, s.id, s._id].filter(Boolean).forEach((id) => {
        const key = String(id);
        if (!stepsById.has(key)) stepsById.set(key, s);
      });
    });

    const analyze = (modes) => {
      const phases = new Set();
      modes.forEach((m) =>
        (m.path || []).forEach((id) => {
          const step = stepsById.get(String(id));
          if (!step) return;
          if (step.phase) phases.add(step.phase);
        }),
      );
      return { phases };
    };

    return {
      manualPhases: analyze(manualModes).phases,
      aiPhases: analyze(aiModes).phases,
    };
  }, [manualModes, aiModes, operationalSteps, elementaryActions]);

  const handleExtraChange = (value) => {
    setExtraContext(value.length > EXTERNAL_MAX ? value.slice(0, EXTERNAL_MAX) : value);
    // Fresh result was computed without the new context — drop it so it can't mislead
    setCritique(null);
  };

  const handleFileImport = (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    setImportError(null);
    const lower = (file.name || '').toLowerCase();
    if (!lower.endsWith('.txt') && !lower.endsWith('.md')) {
      setImportError(t('workshop4.modesOperatoires.compareExternalBadType', 'Seuls les fichiers .txt et .md sont acceptés.'));
      return;
    }
    if (file.size > IMPORT_MAX_BYTES) {
      setImportError(t('workshop4.modesOperatoires.compareExternalTooBig', 'Fichier trop volumineux (max 200 Ko).'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '');
      const sep = extraContext.trim() ? '\n\n' : '';
      handleExtraChange(`${extraContext}${sep}${text}`.slice(0, EXTERNAL_MAX));
      setShowExternal(true);
    };
    reader.onerror = () => {
      setImportError(t('workshop4.modesOperatoires.compareExternalReadError', 'Lecture du fichier impossible.'));
    };
    reader.readAsText(file);
  };

  const runCritique = async () => {
    setIsComparing(true);
    setCritiqueError(null);
    try {
      const response = await api.post('/ai/compare-modes-operatoires', {
        analysisId,
        attackPath,
        businessContext,
        manualModes,
        aiModes,
        operationalSteps,
        elementaryActions,
        extraContext: extraContext.trim().slice(0, EXTERNAL_MAX),
      });
      if (response?.success && response?.data) {
        setCritique(response.data);
        onSaveCritique && onSaveCritique(response.data);
      } else {
        setCritiqueError(t('workshop4.modesOperatoires.compareError', "L'analyse a échoué. Veuillez réessayer."));
      }
    } catch (err) {
      console.error('[ModeComparisonPanel] AI analysis failed:', err);
      setCritiqueError(t('workshop4.modesOperatoires.compareError', "L'Assistant ACG a échoué. Vérifiez que le service AI est actif et réessayez."));
    } finally {
      setIsComparing(false);
    }
  };

  // Fresh run wins over the saved critique
  const result = critique || savedCritique;
  const canAnalyze = aiEnabled && manualModes.length > 0 && aiModes.length > 0;

  const section = (titleKey, items, renderItem) => {
    if (!items || items.length === 0) return null;
    return (
      <div className="mt-4">
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
          {t(titleKey)}
        </div>
        <ul className="space-y-1.5">
          {items.map((item, i) => (
            <li key={i} className="text-sm text-slate-700 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
              {renderItem ? renderItem(item) : item}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div>
      <div className="text-sm font-medium text-slate-600 mb-3">
        {t('workshop4.modesOperatoires.compareModesCount', '{{manual}} {{manualLabel}} • {{ai}} {{aiLabel}}', {
          manual: manualModes.length,
          ai: aiModes.length,
          manualLabel: t('workshop4.modesOperatoires.compareModesCount_user', { count: manualModes.length }),
          aiLabel: t('workshop4.modesOperatoires.compareModesCount_ai', { count: aiModes.length }),
        })}
      </div>

      {/* Phase coverage table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 font-semibold text-sm text-slate-700">
          {t('workshop4.modesOperatoires.comparePhasesCovered', 'Phases couvertes')}
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-500 uppercase tracking-wide bg-slate-50">
              <th className="px-4 py-2 font-medium">Phase</th>
              <th className="px-4 py-2 font-medium text-center">
                {t('workshop4.modesOperatoires.compareUser', 'Utilisateur')}
              </th>
              <th className="px-4 py-2 font-medium text-center">
                {t('workshop4.modesOperatoires.aiBadge', 'Assistant ACG')}
              </th>
            </tr>
          </thead>
          <tbody>
            {PHASES.map((phase) => (
              <tr key={phase} className="border-t border-slate-100">
                <td className="px-4 py-2 font-medium text-slate-700">{phase}</td>
                <td className="px-4 py-2 text-center">
                  {stats.manualPhases.has(phase) ? (
                    <CheckCircle className="h-4 w-4 text-green-500 inline" />
                  ) : (
                    <XCircle className="h-4 w-4 text-slate-300 inline" />
                  )}
                </td>
                <td className="px-4 py-2 text-center">
                  {stats.aiPhases.has(phase) ? (
                    <CheckCircle className="h-4 w-4 text-green-500 inline" />
                  ) : (
                    <XCircle className="h-4 w-4 text-slate-300 inline" />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* General AI analysis */}
      {canAnalyze && (
        <div className="mt-5 border-t border-slate-200 pt-4">
          {/* External analyst context — ephemeral, sent as untrusted DATA only */}
          <div className="mb-4 bg-white rounded-xl border border-slate-200 overflow-hidden">
            <button
              type="button"
              onClick={() => setShowExternal((v) => !v)}
              className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              <span className="inline-flex items-center gap-2">
                <FileText className="h-4 w-4 text-slate-400" />
                {t('workshop4.modesOperatoires.compareExternalTitle', 'Contexte externe (optionnel)')}
                {extraContext.trim() && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold bg-indigo-100 text-indigo-700">
                    {t('workshop4.modesOperatoires.compareExternalActive', 'actif')}
                  </span>
                )}
              </span>
              <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${showExternal ? 'rotate-180' : ''}`} />
            </button>
            {showExternal && (
              <div className="px-4 pb-4">
                <textarea
                  value={extraContext}
                  onChange={(e) => handleExtraChange(e.target.value)}
                  rows={5}
                  maxLength={EXTERNAL_MAX}
                  placeholder={t('workshop4.modesOperatoires.compareExternalPlaceholder', "Collez ici des notes d'audit, extraits CTI, hypothèses métier... (traité comme donnée, jamais comme instruction)")}
                  className="w-full text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-y"
                />
                <div className="mt-1.5 flex items-center justify-between text-xs text-slate-500">
                  <span>{extraContext.length} / {EXTERNAL_MAX}</span>
                  <span className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current && fileInputRef.current.click()}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-200 hover:bg-slate-50 text-slate-600"
                    >
                      <Upload className="h-3 w-3" />
                      {t('workshop4.modesOperatoires.compareExternalImport', 'Importer .txt / .md')}
                    </button>
                    {extraContext && (
                      <button
                        type="button"
                        onClick={() => handleExtraChange('')}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-200 hover:bg-slate-50 text-slate-600"
                      >
                        <X className="h-3 w-3" />
                        {t('workshop4.modesOperatoires.compareExternalClear', 'Effacer')}
                      </button>
                    )}
                  </span>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".txt,.md,text/plain"
                  className="hidden"
                  onChange={handleFileImport}
                />
                {importError && (
                  <div className="mt-2 text-xs text-red-600 flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5" /> {importError}
                  </div>
                )}
                {SUSPICIOUS_PATTERN.test(extraContext) && (
                  <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
                    {t('workshop4.modesOperatoires.compareExternalSuspicious', 'Ce texte ressemble à une instruction : il sera traité comme une simple donnée et ignoré comme instruction.')}
                  </div>
                )}
                <div className="mt-1.5 text-[11px] text-slate-400">
                  {t('workshop4.modesOperatoires.compareExternalPrivacy', 'Non enregistré — envoyé uniquement pour cette analyse. Évitez secrets et données personnelles.')}
                </div>
              </div>
            )}
          </div>
          <button
            onClick={runCritique}
            disabled={isComparing}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-purple-600 text-white hover:bg-purple-700 transition-all disabled:opacity-50"
          >
            <BrainCircuit className="h-4 w-4" />
            {isComparing
              ? t('workshop4.modesOperatoires.compareAnalyzing', "Analyse en cours...")
              : result
                ? t('workshop4.modesOperatoires.compareRegenerate', "Régénérer l'analyse générale")
                : t('workshop4.modesOperatoires.compareAnalyze', "Analyse générale (Assistant ACG)")}
            {isComparing && <Loader2 className="h-4 w-4 animate-spin" />}
          </button>

          {critiqueError && (
            <div className="mt-3 text-sm text-red-600 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" /> {critiqueError}
            </div>
          )}

          {result && !isComparing && (
            <div className="mt-4 bg-white rounded-xl border border-slate-200 p-4">
              {result.overview && (
                <p className="text-sm text-slate-700 leading-relaxed">{result.overview}</p>
              )}
              {section('workshop4.modesOperatoires.compareStrengths', result.strengths)}
              {section('workshop4.modesOperatoires.compareRemarks', result.remarks)}
              {section('workshop4.modesOperatoires.compareAdvice', result.advice)}
              {section(
                'workshop4.modesOperatoires.compareMissingAttacks',
                result.majorMissingAttacks,
                (item) => (
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${severityColor(item.severity)}`}>
                        {item.severity}
                      </span>
                      <span className="font-semibold">{item.attack}</span>
                    </div>
                    {item.why && <div className="mt-1 text-xs text-slate-500">{item.why}</div>}
                  </div>
                ),
              )}
              {section(
                'workshop4.modesOperatoires.compareForgottenVulns',
                result.majorForgottenVulnerabilities,
                (item) => (
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${severityColor(item.severity)}`}>
                        {item.severity}
                      </span>
                      <span className="font-semibold">{item.vulnerability}</span>
                    </div>
                    {item.reasoning && <div className="mt-1 text-xs text-slate-500">{item.reasoning}</div>}
                  </div>
                ),
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ModeComparisonPanel;
